import { Box, Typography, Grid, useMediaQuery, Button } from "@mui/material";
import { DoktorFooter } from "./components/DoktorFooter";
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from "react";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export const DoktorLanding = () => {
    const theme = useTheme();
    const isTabletUp = useMediaQuery( theme.breakpoints.up( 'sm' ) );
    const isLaptopUp = useMediaQuery( theme.breakpoints.up( 'md' ) );
    const navigate = useNavigate();
    const location = useLocation();
    const isSmallHeight = useMediaQuery( '(max-height:700px)' );

    // Extract session_id from URL query parameters
    const queryParams = new URLSearchParams( location.search );
    const sessionId = queryParams.get( 'session_id' );

    const handleStartCheckInClick = () => {
        localStorage.setItem( 'landing_url', '/doktorlanding' );

        // If we have a session ID, preserve it and pass it to the next page
        if ( sessionId ) {
            localStorage.setItem( 'session-id', sessionId );
            navigate( '/doktor-demographics?session_id=' + sessionId );
        } else {
            navigate( '/doktor-demographics' );
        }
    };

    useEffect( () => {
        localStorage.clear()
    }, [] )

    const listItems = [
        "Heart Rate",
        "Breathing Rate",
        "Blood Pressure",
        "Heart Rate Variability",
        "Cardiac Workload",
        "Mental Stress Index",
        "Cardiovascular Disease Risk",
        "Overall Wellness Percentile"
    ];

    const ListItem = ( { item } ) => (
        <Box display="flex" alignItems="center" mb={ '5px' }>
            <CheckCircleIcon sx={ { color: "#0633CB", width: "20px", marginRight: "8px" } }></CheckCircleIcon>
            <Typography
                color="#000"
                variant="body2"
                sx={ {
                    fontSize: isLaptopUp ? '16px' : 'clamp(14px, 3vw, 15px)',
                    fontWeight: '400',
                } }
            >
                { item }
            </Typography>
        </Box>
    );

    const renderList = () => {
        if ( isLaptopUp ) {
            return (
                <Grid container spacing={ 0 } justifyContent={ isLaptopUp ? 'center' : 'start' } sx={ { mb: isLaptopUp ? 4 : 1, ml: 10 } }>
                    { [ ...Array( 2 ) ].map( ( _, colIndex ) => (
                        <Grid item xs={ 6 } key={ colIndex }>
                            { listItems.slice( colIndex * 4, ( colIndex + 1 ) * 4 ).map( ( item, index ) => (
                                <ListItem key={ index } item={ item } />
                            ) ) }
                        </Grid>
                    ) ) }
                </Grid>
            );
        } else {
            return (
                <Box sx={ { display: 'flex', flexDirection: 'column', alignItems: `${ isLaptopUp ? 'center' : 'start' }`, mb: isLaptopUp ? 4 : 0 } }>
                    { listItems.map( ( item, index ) => (
                        <ListItem key={ index } item={ item } />
                    ) ) }
                </Box>
            );
        }
    };

    return (
        <Box
            sx={ {
                minHeight: isLaptopUp ? '100vh' : ( isSmallHeight ? "85vh" : "90vh" ),
                display: 'flex',
                flexDirection: 'column',
                maxWidth: isLaptopUp ? '1000px' : '100%',
                margin: '0 auto',
                padding: isTabletUp ? '32px' : '16px',
                boxSizing: 'border-box',
            } }
        >
            <Box
                sx={ {
                    flex: isTabletUp ? 1 : 0.7,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: isTabletUp ? '800px' : '100%',
                    margin: '0 auto',
                } }
            >
                <Box>
                    <Typography
                        color="#232A67"
                        variant="h1"
                        sx={ {
                            fontSize: isLaptopUp ? '36px' : 'clamp(26px, 5vw, 32px)',
                            fontWeight: '700',
                            lineHeight: 1.2,
                            mb: isLaptopUp ? 3 : 1,
                            textAlign: isLaptopUp ? 'center' : 'start',
                        } }
                    >
                        Start your appointment check-in
                    </Typography>
                    <Typography
                        color="#000"
                        variant="body1"
                        sx={ {
                            fontSize: isLaptopUp ? '18px' : 'clamp(14px, 3vw, 16px)',
                            fontWeight: '400',
                            lineHeight: 1.5,
                            mb: 2,
                            textAlign: isLaptopUp ? 'center' : 'start',
                        } }
                    >
                        By completing this self-service check in, you will reduce your time in your virtual visit by 15-20 minutes.
                    </Typography>
                    <Typography
                        color="#000"
                        variant="body1"
                        sx={ {
                            fontSize: isLaptopUp ? '18px' : 'clamp(14px, 3vw, 16px)',
                            fontWeight: '500',
                            lineHeight: 1.5,
                            mb: 2,
                            textAlign: isLaptopUp ? 'center' : 'start',
                        } }
                    >
                        You'll also learn:
                    </Typography>
                    { renderList() }
                </Box>

                <Box sx={ { display: 'flex', flexDirection: 'column', alignItems: 'center', mt: isLaptopUp ? 1 : 2 } }>
                    <Typography
                        color="#000"
                        variant="body2"
                        sx={ {
                            fontSize: isLaptopUp ? '16px' : 'clamp(14px, 3vw, 15px)',
                            fontWeight: '400',
                            mb: 1,
                            textAlign: 'center',
                        } }
                    >
                        It takes 2-3 minutes.
                    </Typography>
                    <Button
                        onClick={ handleStartCheckInClick }
                        style={ {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            maxWidth: '300px',
                            height: '48px',
                            textDecoration: 'none',
                            background: '#0633CB',
                            color: '#FFF',
                            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                            borderRadius: '8px',
                            fontSize: isLaptopUp ? '18px' : '16px',
                            fontWeight: '600',
                            marginBottom: '10px',
                        } }
                    >
                        Start Check In
                    </Button>
                </Box>
            </Box>
            <Box sx={ { width: '100%', display: 'flex', justifyContent: 'center' } }>
                <DoktorFooter />
            </Box>
        </Box>
    );
};